import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '../../../mocks/movies';

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  const goTo = useCallback(
    (index: number) => {
      setActive(((index % total) + total) % total);
    },
    [total],
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((current) => (current + 1) % total);
    }, 6000);
    return () => clearInterval(timer);
  }, [total, active]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-secondary-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点影视推荐"
    >
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title}高清中文字幕在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4 animate-fade-up">
              <span className="px-3 py-1 rounded-full bg-accent-500 text-secondary-950 text-xs font-bold">编辑力荐</span>
              <span className="flex items-center gap-1 text-background-100 text-sm">
                <i className="ri-star-fill text-accent-400" aria-hidden="true"></i>
                {heroSlides[active].rating.toFixed(1)}
              </span>
              <span className="text-background-300 text-sm">{heroSlides[active].year}</span>
            </div>
            <h1 className="font-heading font-extrabold text-background-50 text-3xl md:text-5xl leading-tight mb-3 animate-fade-up">
              {heroSlides[active].title}
            </h1>
            <p className="text-background-100 text-sm md:text-lg mb-2 animate-fade-up">{heroSlides[active].subtitle}</p>
            <p className="text-background-300 text-xs md:text-sm leading-relaxed mb-6 max-w-xl line-clamp-2 animate-fade-up">
              {heroSlides[active].short}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${heroSlides[active].id}`}
                className="flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-secondary-950 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-xl" aria-hidden="true"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${encodeURIComponent(heroSlides[active].genres[0])}`}
                className="flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-medium whitespace-nowrap backdrop-blur transition-colors cursor-pointer"
              >
                <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                {heroSlides[active].genres[0]}片单
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={prev}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 backdrop-blur transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={next}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 backdrop-blur transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}